import { GoBackAnchor, If } from '@paytrace/pt-skel/Component'
import { AppCtx, AppCtxI } from '@paytrace/pt-skel/Context'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { toInteger } from 'lodash'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { EDAlertDialog } from '../../../Component/EnhancedData/EDAlertDialog'
import {
  BackendErrorFeedback,
  SuccessFeedback,
  ValidationErrorFeedback,
} from '../../../Component/EnhancedData/EDResponseFeedbacks'
import { OrangeButton } from '../../../Component/EnhancedData/OrangeButton'
import { DataWithKey, RowInputChangeHandler } from '../../../Component/EnhancedData/Types'
import SectionContainer from '../../../Component/SectionContainer'
import Event from '../../../Event'
import {
  defaultMEDFormStateData,
  emptyEnhancedDataBackendError,
  emptyEnhancedDataValidationError,
  initialProductItem,
  MSG_L2_ENHANCED_DATA_SUCCESS,
  MSG_L3_ENHANCED_DATA_SUCCESS,
  NETWORK_ERROR_BANNER_MESSAGE,
  PLUS_CREATE_NEW_PRODUCT_ITEM_ELEM_ID,
  SEARCH_PRODUCT_LIMIT,
} from '../../../Helper/EnhancedData/Constants'
import {
  getNextIndex,
  hasBackendError,
  hasValidationError, toConsumableNumber,
  toTarget,
} from '../../../Helper/EnhancedData/EnhancedDataUtils'
import {
  addLineItemToMap,
  clearLineItemTaxAmountsFromMap,
  getUpdatedLineItemMap,
  LINE_ITEM_FIELD_MAP,
  removeLineItemFromMap,
  toEnhancedDataRequest,
  toMappedBackendValidationError,
  toMEDFormData,
} from '../../../Helper/EnhancedData/ManageEnhancedDataMapper'
import { formatter } from '../../../Helper/Money'
import { clearNetworkError, getViewTransactionDetailHref, raiseNetworkError } from '../../../Helper/util'
import { useNetworkErrorInterceptor } from '../../../hooks/useNetworkErrorInterceptor'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangeGuard'
import * as EDT from '../../../Type/EnhancedData'
import { LineItemRowErrorMap, TaxOptionType } from '../../../Type/EnhancedData'
import { EnhancedDataPageTemplate, TemplateProps } from '../EnhancedDataPageTemplate'
import { LevelThreeSection } from './LevelThreeSection'
import { LevelTwoSummarySection } from './LevelTwoSummarySection'
import { TransactionSummarySection } from './TransactionSummarySection'
import { PopupLink } from '../../../Component/PopupWindow'

const self = Symbol('ManageEnhancedData')
// Dedicated symbol ensures search subscriptions are never killed when `self` unsubs
const searchSubscriber = Symbol('ManageEnhancedDataSearch')

const HELP_URL = 'https://paytrace.atlassian.net/wiki/spaces/HD/pages/3964764227/Enhanced+Data+Page+fields'
const PAGE_TITLE: string = 'Manage Enhanced Data'

interface ManageEnhancedDataProps {
  data?: any
  customerId?: number
  transactionId: number
  cardLevel?: number | string
}

const InPageErrorBanner = ({ message, templateProps }: { message: string; templateProps: TemplateProps }): React.JSX.Element => (
  <EnhancedDataPageTemplate { ...templateProps }>
    <div className="error feedback"> { message } </div>
  </EnhancedDataPageTemplate>
)

const MissingLineItemAlert = ({ showModal, onClose }: { showModal: boolean, onClose: () => void }): React.JSX.Element => {
  return (
    <EDAlertDialog
        isVisible={showModal}
        title='Missing Line Item'
        text='You must add at least one product line item to this transaction.'
        className='enhanced-data'
        onCancel={onClose}
        cancelButtonLabel='Add Line Items'
    />
  )
}

const EnhancedDataTotalMismatchAlert = ({ showModal, onClose, totalsMismatchMessageContent }: { showModal: boolean, onClose: () => void, totalsMismatchMessageContent: React.JSX.Element }): React.JSX.Element => {
  const fullMessageContent = (
    <>
      The Enhanced Data Total of all product line items and fees must exactly match the Original Transaction Total in order to save enhanced data.
      <br /><br />
      {totalsMismatchMessageContent}
    </>
  )
  return (
    <EDAlertDialog
        isVisible={showModal}
        title="Totals don't match"
        text={fullMessageContent}
        className='enhanced-data'
        onCancel={onClose}
        cancelButtonLabel='Review Line Items'
    />
  )
}

const UnsavedChangesModal = ({
  showModal,
  onStay,
  onLeave,
}: {
  showModal: boolean
  onStay: () => void
  onLeave: () => void
}): React.JSX.Element => {
  const messageContent = (
    <>
      Are you sure you want to leave this page? Changes you made will not be saved.
      <br /><br />
      If you do not submit all of the enhanced data for this transaction in a timely manner, it may not qualify for the best processing rates.
    </>
  )

  return (
    <EDAlertDialog
      isVisible={showModal}
      title="Unsaved Changes"
      text={messageContent}
      className="enhanced-data unsaved-changes-modal"
      onCancel={onStay}
      cancelButtonLabel="Cancel"
      onConfirm={onLeave}
      confirmButtonLabel="Leave This Page"
    />
  )
}

export function ManageEnhancedData (props: ManageEnhancedDataProps): React.JSX.Element {
  const { transactionId } = props
  const appContext = useContext<AppCtxI>(AppCtx)

  // Single Hook Execution
  useNetworkErrorInterceptor(NETWORK_ERROR_BANNER_MESSAGE)

  const [transactionDetails, setTransactionDetails] = useState<EDT.TransactionSearchRecordForManualFill>()
  const [medFormData, setMedFormData] = useState<EDT.MEDFormStateData>(defaultMEDFormStateData)
  const [isTaxExempt, setIsTaxExempt] = useState(false)
  const [showFullPage, setShowFullPage] = useState<boolean>(true)
  const [successfulResponseMessage, setSuccessfulResponseMessage] = useState<string>('')
  const [ehancedDataTotalMismatch, setEhancedDataTotalMismatch] = useState<number>(0)
  const [hasMatchingTotals, setHasMatchingTotals] = useState<boolean>(false)
  const [backendErrorResponse, setBackendErrorResponse] = useState<EDT.EnhancedDataBackendError>(emptyEnhancedDataBackendError)
  const [validationError, setValidationError] = useState<EDT.EnhancedDataValidationError>(emptyEnhancedDataValidationError)
  const [showValidationErrorBanner, setShowValidationErrorBanner] = useState<boolean>(false)
  const [showMissingLineItemModal, setShowMissingLineItemModal] = useState<boolean>(false)
  const [showTotalMismatchModal, setShowTotalMismatchModal] = useState<boolean>(false)
  const [showTotalMismatchBanner, setShowTotalMismatchBanner] = useState<boolean>(false)
  const [isSaveBtnClicked, setIsSaveBtnClicked] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  // Keep track of which radio option is selected for Tax
  const [selectedTaxOption, setSelectedTaxOption] = useState<EDT.TaxOptionType>('transaction')

  // Track product search results and pagination status per row
  const [searchResultsMap, setSearchResultsMap] = useState<Record<string | number, EDT.LineItemStateData[]>>({})
  const [hasMoreProductsMap, setHasMoreProductsMap] = useState<Record<string | number, boolean>>({})

  // Track active search row & pagination metadata without causing parent re-renders
  const activeSearchRowKeyRef = useRef<string | number | null>(null)
  const searchMetaRef = useRef<Record<string | number, { keyword?: string; offset: number }>>({})

  // Track Merchant and Customer TaxIds as they are Masked initially
  const customerTaxIdRef = useRef<string>('')
  const merchantTaxIdRef = useRef<string>('')
  const originalTaxAmountRef = useRef<number>(0)

  // 🚀 Unsaved changes guard custom hook
  const {
    showModal: showUnsavedChangesModal,
    handleStay: handleUnsavedStay,
    handleLeave: handleUnsavedLeave,
    setBaseline,
    markClean,
  } = useUnsavedChangesGuard(medFormData, [PLUS_CREATE_NEW_PRODUCT_ITEM_ELEM_ID])

  const merchantId: number = toInteger(appContext.merchant)
  const cardLevel = transactionDetails?.card_level ?? props.cardLevel ?? 1
  const isLevel3Card: boolean = toInteger(cardLevel) === 3
  const hasLineItem: boolean = Object.keys(medFormData.lineItemMap || {}).length > 0
  const isEligibleForSubmission = hasLineItem && !hasValidationError(validationError) && hasMatchingTotals

  const clearResponses = (): void => {
    setSuccessfulResponseMessage('')
    setBackendErrorResponse(emptyEnhancedDataBackendError)
    clearNetworkError()
  }

  const fetchTransactionDetailsById = useCallback(() => {
    PubSub.pub(Event.GET_TRANSACTION_DETAILS_FOR_MANUAL_FILL_REQUEST, { transactionId, merchantId })
  }, [transactionId, merchantId])

  const saveEnhancedData = useCallback((currentFormData: EDT.MEDFormStateData) => {
    setIsSaving(true)
    const formDataToSave: EDT.MEDFormStateData = {
      ...currentFormData,
      customerTaxId: customerTaxIdRef.current,
      merchantTaxId: merchantTaxIdRef.current,
    }
    const requestPayload: EDT.EnhancedDataRequest = toEnhancedDataRequest(
      merchantId,
      transactionDetails,
      formDataToSave
    )
    PubSub.pub(Event.SAVE_ENHANCED_DATA_REQUEST, transactionId, requestPayload)
  }, [transactionId, merchantId, transactionDetails])

  // Clear stale network errors on mount & fetch transaction details
  useEffect(() => {
    clearNetworkError()
    if (transactionId) {
      fetchTransactionDetailsById()
    }
  }, [fetchTransactionDetailsById, transactionId])

  const handleErrorResponse = useCallback((rawError: any): void => {
    setIsSaving(false)
    const parsedError = toMappedBackendValidationError(rawError)
    if (parsedError) {
      setBackendErrorResponse(parsedError)
    } else {
      raiseNetworkError(NETWORK_ERROR_BANNER_MESSAGE)
    }
  }, [])

  useEffect(() => {
    PubSub.sub(self, Event.GET_TRANSACTION_DETAILS_FOR_MANUAL_FILL_RESPONSE, (tranRecordForManualFill: EDT.TransactionSearchRecordForManualFill) => {
      setTransactionDetails(tranRecordForManualFill)
      const formData: EDT.MEDFormStateData = toMEDFormData(tranRecordForManualFill)
      const lineItems = tranRecordForManualFill?.line_items ?? []
      setMedFormData({ ...formData })

      // Save initial transaction tax amount to ref
      originalTaxAmountRef.current = formData?.taxAmount ?? tranRecordForManualFill?.tax_amount ?? 0

      const is_tax_exempt: boolean = Boolean(tranRecordForManualFill.is_tax_exempt)
      const hasTaxedLineItem: boolean = lineItems.some((li: EDT.LineItem) => (li.item_tax_amount ?? 0) > 0)

      if (is_tax_exempt) {
        setIsTaxExempt(true)
        setSelectedTaxOption('exempt')
      } else if (hasTaxedLineItem) {
        setSelectedTaxOption('line-item')
      }

      merchantTaxIdRef.current = tranRecordForManualFill?.merchant_tax_id || ''
      customerTaxIdRef.current = tranRecordForManualFill?.customer_tax_id || ''

      // Set initial baseline snapshot inside hook
      setBaseline(formData)
    })

    PubSub.sub(self, Event.SAVE_ENHANCED_DATA_RESPONSE, () => {
      setSuccessfulResponseMessage(
        isLevel3Card ? MSG_L3_ENHANCED_DATA_SUCCESS : MSG_L2_ENHANCED_DATA_SUCCESS
      )
      setIsSaving(false)
      // Mark form clean on successful save
      markClean()
    })

    PubSub.sub(self, Event.SAVE_ENHANCED_DATA_RESPONSE_ERROR, (rawError: any) => {
      handleErrorResponse(rawError)
    })

    return () => { PubSub.unsub(self) }
  }, [cardLevel, isLevel3Card, handleErrorResponse, setBaseline, markClean, setIsTaxExempt, setSelectedTaxOption])

  const handleMEDInputChange = useCallback((fieldName: string, fieldValue: any) => {
    setShowValidationErrorBanner(false)
    setMedFormData((prevState) => ({ ...prevState, [fieldName]: fieldValue }))
    const medFormDataField = fieldName as keyof EDT.MEDFormStateData
    if (medFormDataField === ('merchantTaxId' as keyof EDT.MEDFormStateData)) {
      merchantTaxIdRef.current = fieldValue
    }
    if (medFormDataField === ('customerTaxId' as keyof EDT.MEDFormStateData)) {
      customerTaxIdRef.current = fieldValue
    }
  }, [])

  const handleLineItemUpdate: RowInputChangeHandler<EDT.LineItemStateData> = useCallback((key, fieldName, updatedValue) => {
    setMedFormData(prev => {
      if (!prev.lineItemMap || !prev.lineItemMap[key]) return prev
      return {
        ...prev,
        lineItemMap: getUpdatedLineItemMap(prev.lineItemMap, key, fieldName, updatedValue),
      }
    })
  }, [])

  const handleLineItemDelete = useCallback((key: string | number): Record<string, DataWithKey<EDT.LineItemStateData>> => {
    const updatedMap = removeLineItemFromMap(medFormData.lineItemMap ?? {}, key)
    setMedFormData(prev => ({ ...prev, lineItemMap: updatedMap }))
    return updatedMap
  }, [medFormData.lineItemMap])

  const handleLineItemAdd = useCallback((lineItemWithKey: DataWithKey<EDT.LineItemStateData>) => {
    if (lineItemWithKey.index === -1) {
      lineItemWithKey.index = getNextIndex(medFormData.lineItemMap)
      lineItemWithKey.rowNumber = (lineItemWithKey.index + 1)
    }
    setMedFormData(prev => ({
      ...prev,
      lineItemMap: addLineItemToMap(lineItemWithKey, prev.lineItemMap),
    }))
  }, [medFormData.lineItemMap])

  const handleTaxOptionChange = useCallback((value: TaxOptionType) => {
    setSelectedTaxOption(value)
    setIsTaxExempt(value === 'exempt')

    setMedFormData((prev) => ({
      ...prev,
      taxAmount: value === 'transaction' ? originalTaxAmountRef.current : 0,
    }))
  }, [setIsTaxExempt, setSelectedTaxOption, setMedFormData])

  const handleInputFieldError = useCallback((fieldName: string, errorMessage: string | null): void => {
    setValidationError(prev => {
      const updatedInputErrors = { ...prev.inputErrors }
      if (errorMessage) {
        updatedInputErrors[fieldName] = errorMessage
      } else {
        delete updatedInputErrors[fieldName]
      }
      return {
        ...prev,
        inputErrors: updatedInputErrors,
      }
    })
    // setShowValidationErrorBanner(false)
  }, [])

  const handleLineItemRowError = useCallback((rowErrorMap: LineItemRowErrorMap): void => {
    setValidationError(prev => {
      return {
        ...prev,
        lineItemErrors: rowErrorMap,
      }
    })
    // setShowValidationErrorBanner(false)
  }, [])

  const handleEnhancedDataTotalChange = useCallback((currentEnhancedDataTotal: number): void => {
    const transactionAmount: number = toConsumableNumber(transactionDetails?.settle_amount)
    const difference: number = toConsumableNumber(currentEnhancedDataTotal - transactionAmount)
    const isMatched = difference === 0
    setHasMatchingTotals(isMatched)
    setEhancedDataTotalMismatch(difference)
    // Hide banner as soon as the totals match
    if (isMatched) setShowTotalMismatchBanner(false)
  }, [transactionDetails])

  const totalsMismatchMessageContent: React.JSX.Element = useMemo(() => {
    const totalMismatchDisplayValue: string = formatter.format(ehancedDataTotalMismatch)
    if (ehancedDataTotalMismatch < 0) {
      return (
        <span>
          <strong> {totalMismatchDisplayValue}</strong> is missing from your Enhanced Data total.
        </span>
      )
    } if (ehancedDataTotalMismatch > 0) {
      return (
        <span>
          Your Enhanced Data total exceeds the transaction total by <strong> {totalMismatchDisplayValue}</strong>.
        </span>
      )
    }
    return (<></>)
  }, [ehancedDataTotalMismatch])

  const getFormDataWithoutLineItemTax = useCallback(() => {
    const updatedMap = clearLineItemTaxAmountsFromMap(medFormData.lineItemMap ?? {})

    return { ...medFormData, lineItemMap: updatedMap }
  }, [medFormData])

  const handleFormSubmission = useCallback((e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsSaveBtnClicked(true)
    clearResponses()
    if (!hasLineItem) {
      setShowMissingLineItemModal(true)
    }
    if (!hasMatchingTotals) {
      setShowTotalMismatchModal(true)
      setShowTotalMismatchBanner(true)
    }
    if (hasValidationError(validationError)) {
      setShowValidationErrorBanner(true)
    }

    if (isEligibleForSubmission) {
      let currentFormData = medFormData
      if (selectedTaxOption !== 'line-item') {
        currentFormData = getFormDataWithoutLineItemTax()
      }
      saveEnhancedData(currentFormData)
    }
  }, [hasLineItem, validationError, isEligibleForSubmission, saveEnhancedData, hasMatchingTotals, selectedTaxOption, getFormDataWithoutLineItemTax, medFormData])

  // Hide error banner when the banner is already visible but there is no error left on the UI
  useEffect(() => {
    if (showValidationErrorBanner && !hasValidationError(validationError)) {
      setShowValidationErrorBanner(false)
      setIsSaveBtnClicked(false)
    }
  }, [showValidationErrorBanner, validationError])

  const searchProductByKeyword = useCallback(
    (keyword: string = '', rowKey: string | number, offset: number = 0): void => {
      const stringRowKey = String(rowKey)
      const trimmedKeyword = keyword.trim()

      activeSearchRowKeyRef.current = stringRowKey
      searchMetaRef.current[stringRowKey] = { keyword: trimmedKeyword, offset }

      PubSub.pub(Event.SEARCH_PRODUCTS_REQUEST, {
        merchantId,
        keyword: trimmedKeyword,
        offset,
        limit: SEARCH_PRODUCT_LIMIT,
      })
    },
    [merchantId]
  )

  const handleProductSearchResponse = useCallback((response: any) => {
    const rowKey = activeSearchRowKeyRef.current ? String(activeSearchRowKeyRef.current) : null
    if (!rowKey) return

    const rawList: any[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.products)
          ? response.products
          : []

    const mappedProducts = rawList.map((item) =>
      toTarget<any, EDT.LineItemStateData>(
        item,
        LINE_ITEM_FIELD_MAP,
        initialProductItem
      )
    )

    setSearchResultsMap(prev => {
      const isPagination = (searchMetaRef.current[rowKey]?.offset || 0) > 0
      const existing = prev[rowKey] || []
      return {
        ...prev,
        [rowKey]: isPagination ? [...existing, ...mappedProducts] : mappedProducts,
      }
    })

    setHasMoreProductsMap(prev => ({
      ...prev,
      [rowKey]: rawList.length >= SEARCH_PRODUCT_LIMIT,
    }))
  }, [])

  useEffect(() => {
    PubSub.sub(searchSubscriber, Event.SEARCH_PRODUCTS_RESPONSE, handleProductSearchResponse)
    return () => { PubSub.unsub(searchSubscriber) }
  }, [handleProductSearchResponse])

  const handleSearchKeywordChange = useCallback((keyword: string, rowKey: string | number, offset: number = 0) => {
    searchProductByKeyword(keyword, rowKey, offset)
  }, [searchProductByKeyword])

  const handleLoadMore = useCallback(
    (rowKey: string | number, nextOffset: number) => {
      const stringRowKey = String(rowKey)
      const currentKeyword = searchMetaRef.current[stringRowKey]?.keyword || ''
      searchProductByKeyword(currentKeyword, rowKey, nextOffset)
    },
    [searchProductByKeyword]
  )

  const templateProps: TemplateProps = {
    title: PAGE_TITLE,
    redirectUrl: '',
    helpUrl: HELP_URL,
    symbol: self,
  }

  if (!transactionId) {
    return <InPageErrorBanner message="Please provide a valid transaction ID" templateProps={templateProps}/>
  }

  const linkText: string = 'View Transaction Details'
  const hrefValue: string = getViewTransactionDetailHref(transactionId)

  return (
    <EnhancedDataPageTemplate {...templateProps}>
      <GoBackAnchor/>
      <SectionContainer title="Transaction Summary" className="field-section">
        <TransactionSummarySection transactionDetail={transactionDetails}>
          <tr className="transaction-summary-row">
            <td colSpan={2} className="transaction-summary-cell">
              <PopupLink className='strong-anchor' href={hrefValue}>{linkText}</PopupLink>
            </td>
          </tr>
        </TransactionSummarySection>
      </SectionContainer>
      <form
        id="manage-enhanced-data-form"
        className="manage-enhanced-data-form"
        autoComplete="off"
        onSubmit={handleFormSubmission}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
            e.preventDefault()
          }
        }}
      >
        <LevelTwoSummarySection
          appContext={appContext}
          formData={medFormData}
          isPartialPage={!showFullPage}
          isTaxExempt={isTaxExempt}
          onInputChange={handleMEDInputChange}
          onTaxExemptChange={(isChecked: boolean) => setIsTaxExempt(isChecked)}
          onAddLineItemNavClick={(isVisible: boolean) => setShowFullPage(isVisible)}
          selectedTaxOption={selectedTaxOption}
          onInputError={handleInputFieldError}
          backendError={backendErrorResponse}
          validationError={validationError}
          showValidationErrorBanner={showValidationErrorBanner}
          isSaveButtonClicked={isSaveBtnClicked}
        />
        <div className='horizontal-break'/>
        <If is={showFullPage}>
          <LevelThreeSection
            formData={medFormData}
            transactionAmount={Number(transactionDetails?.settle_amount) || 0}
            isTaxExempt={isTaxExempt}
            onInputChange={handleMEDInputChange}
            onLineItemUpdate={handleLineItemUpdate}
            onLineItemDelete={handleLineItemDelete}
            onLineItemAdd={handleLineItemAdd}
            onTaxExemptChange={(isChecked: boolean) => setIsTaxExempt(isChecked)}
            selectedTaxOption={selectedTaxOption}
            onTaxOptionChange={handleTaxOptionChange}
            onInputError={handleInputFieldError}
            onLineItemRowError={handleLineItemRowError}
            onEnhancedDataTotalChange={handleEnhancedDataTotalChange}
            hasMatchingTotals={hasMatchingTotals}
            totalsMismatchMessageContent={totalsMismatchMessageContent}
            showTotalMismatchBanner={showTotalMismatchBanner}
            highlightRowsOnError={showValidationErrorBanner}
            searchResultsMap={searchResultsMap}
            hasMoreProductsMap={hasMoreProductsMap}
            onSearchKeywordChange={handleSearchKeywordChange}
            onLoadMore={handleLoadMore}
          />
        </If>
        <div className='horizontal-break'/>
        { successfulResponseMessage &&
          (
            <>
              <SuccessFeedback message={successfulResponseMessage}/>
              <div className="horizontal-break"/>
            </>
          )
        }
        { hasBackendError(backendErrorResponse) &&
          (
            <>
              <BackendErrorFeedback {...backendErrorResponse}/>
              <div className="horizontal-break"/>
            </>
          )
        }
        <If is={showValidationErrorBanner}>
          <ValidationErrorFeedback {...validationError}/>
        </If>
        <OrangeButton
          id='save-enhanced-data-btn'
          title={isSaving ? 'Saving...' : 'Save Enhanced Data'}
          disabled={isSaving}
        />
      </form>
      <MissingLineItemAlert
        showModal={showMissingLineItemModal}
        onClose={() => setShowMissingLineItemModal(false)}
      />
      <EnhancedDataTotalMismatchAlert
        showModal={showTotalMismatchModal}
        onClose={() => setShowTotalMismatchModal(false)}
        totalsMismatchMessageContent={totalsMismatchMessageContent}
      />
      <UnsavedChangesModal
        showModal={showUnsavedChangesModal}
        onStay={handleUnsavedStay}
        onLeave={handleUnsavedLeave}
      />
      <GoBackAnchor/>
    </EnhancedDataPageTemplate>
  )
}
